import { ActionMode, GeneralStatusEnum, KindergartenDetailsTabs } from '../../../common/enums/common.enum';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Breadcrumbs, Link, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import GenericButton, { GenericButtonTypeEnum } from '../../../components/GenericButton';
import GenericTextField from '../../../components/GenericTextField/GenericTextField';
import GenericStatusSwitch from '../../../components/GenericStatusSwitch';
import { createNotification, getStatusColor } from '../../../utilities/helper/notification.helper';
import { CustomErrorStatus, MESSAGE_TYPE } from '../../../common/constants/common.constants';
import { createParent, fetchChildrenBasicInfo, getParentDetails, updateParent } from '../../../api/kindergarten.api';
import { ChildBasicInfo, CreateParentDto, ParentDto, UpdateParentDto } from '../../../common/interfaces/kindergarten.interface';
import { ReactComponent as EditIcon } from '../../../assets/edit-icon.svg';
import { ReactComponent as CircleIcon } from '../../../assets/circle-icon.svg';
import GenericPeopleDropdown from '../../../components/GenericPeopleDropdown';
import GenericInfoModal from '../../../components/GenericInfoModal';
import { ProfilePageBox, ProfilePictureAvatar } from '../../../common/styles/Common.styles';

interface ParentPageProps {
  mode: ActionMode;
}

const ParentPage: React.FC<ParentPageProps> = ({ mode }) => {
  const { parentId, kindergartenId, kindergartenName } = useParams<{
    parentId?: string;
    kindergartenId: string;
    kindergartenName: string;
  }>();

  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState<GeneralStatusEnum>(GeneralStatusEnum.ACTIVE);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [children, setChildren] = useState<ChildBasicInfo[]>([]);
  const [childrenOptions, setChildrenOptions] = useState<ChildBasicInfo[]>();
  const [initialParent, setInitialParent] = useState<ParentDto>();

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [pendingChildren, setPendingChildren] = useState<ChildBasicInfo[]>([]);
  const [isEditing, setIsEditing] = useState(mode === ActionMode.CREATE || mode === ActionMode.EDIT);
  const [currentMode, setCurrentMode] = useState<ActionMode>(mode);

  useEffect(() => {
    getChildren();
  }, [kindergartenId]);

  useEffect(() => {
    if (childrenOptions) {
      if (currentMode === ActionMode.EDIT || (currentMode === ActionMode.DETAILS && parentId)) {
        fetchParentDetails();
      }
    }
  }, [kindergartenId, mode, parentId, childrenOptions]);

  const handleSave = async () => {
    const success = await saveParent();
    if (success) {
      navigate(`/${kindergartenId}/details?tab=${KindergartenDetailsTabs.Parents}`);
    }
  };

  const getChildren = async () => {
    try {
      if (!kindergartenId) return;
      const response = await fetchChildrenBasicInfo(kindergartenId);
      setChildrenOptions(response.results ?? []);
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'Failed to fetch children.');
    }
  };

  const handleSaveAndCreateAnother = async () => {
    const success = await saveParent();
    if (success) {
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setStatus(GeneralStatusEnum.ACTIVE);
      setChildren([]);
    }
  };

  const saveParent = async () => {
    if (!kindergartenId) return;

    if (!firstName.trim()) {
      createNotification(MESSAGE_TYPE.ERROR, 'First name is required');
      return false;
    }
    if (!lastName.trim()) {
      createNotification(MESSAGE_TYPE.ERROR, 'Last name is required');
      return false;
    }

    if (currentMode === ActionMode.CREATE) {
      if (!email.trim()) {
        createNotification(MESSAGE_TYPE.ERROR, 'Email name is required');
        return false;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        createNotification(MESSAGE_TYPE.ERROR, 'Please enter a valid email address');
        return false;
      }
    }
    try {
      if (currentMode === ActionMode.CREATE) {
        const parentPayload: CreateParentDto = {
          email: email,
          firstName: firstName,
          lastName: lastName,
          phoneNumber: phone,
          status: status
        };
        if (children.length > 0) {
          parentPayload.childIds = children.map(c => c.id);
        }
        const response = await createParent(kindergartenId, parentPayload);
        createNotification(MESSAGE_TYPE.SUCCESS, response?.message ? response.message : 'Parent created successfully');
      } else if (currentMode === ActionMode.EDIT && parentId) {
        const parentPayload: UpdateParentDto = {};
        if (firstName !== initialParent?.firstName) parentPayload.firstName = firstName;
        if (lastName !== initialParent?.lastName) parentPayload.lastName = lastName;
        if (phone !== initialParent?.phoneNumber) parentPayload.phoneNumber = phone;
        if (status !== initialParent?.status) parentPayload.status = status;
        const initialChildIds = initialParent?.children?.map(c => c.id) || [];
        const currentChildIds = children.map(c => c.id);
        const childrenChanged =
          initialChildIds.length !== currentChildIds.length || !initialChildIds.every(id => currentChildIds.includes(id));

        if (childrenChanged) {
          parentPayload.childIds = currentChildIds;
        }
        if (Object.keys(parentPayload).length > 0) {
          const response = await updateParent(kindergartenId, parentId, parentPayload);
          createNotification(MESSAGE_TYPE.SUCCESS, response?.message ?? 'Parent updated successfully');
        }
      }
      return true;
    } catch (error: any) {
      const backendStatus = error?.response?.data?.status;
      if (backendStatus === CustomErrorStatus.USER_EMAIL_ALREADY_EXISTS) {
        createNotification(MESSAGE_TYPE.ERROR, 'User with provided email is already register in this kindergarten');
      } else {
        const errorMessage = error?.response?.data?.message ?? error?.message ?? 'Failed to create parent';
        createNotification(MESSAGE_TYPE.ERROR, errorMessage);
      }
      return false;
    }
  };
  const fetchParentDetails = async () => {
    if (!kindergartenId || !parentId) return;
    try {
      const response = await getParentDetails(kindergartenId, parentId);
      if (response.results) {
        const parent = response.results;
        setInitialParent(parent);
        setFirstName(parent.firstName);
        setLastName(parent.lastName);
        setEmail(parent.email);
        setPhone(parent.phoneNumber ?? '');
        setStatus(parent.status);
        setAvatarUrl(parent.profileImage ?? null);

        if (parent.children?.length) {
          const mappedChildren = parent.children.map(c => childrenOptions?.find(co => co.id === c.id) || c);
          setChildren(mappedChildren);
        } else {
          setChildren([]);
        }
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'Failed to fetch parent details');
    }
  };

  const getPageTitle = () => {
    switch (currentMode) {
      case ActionMode.CREATE:
        return 'Create parent';
      case ActionMode.EDIT:
        return 'Edit parent';
      case ActionMode.DETAILS:
        return `${firstName} ${lastName}`;
    }
  };

  const handleChildrenChange = (newChildren: ChildBasicInfo[]) => {
    const removed = children.filter(c => !newChildren.some(nc => nc.id === c.id));
    if (removed.length > 0) {
      setPendingChildren(newChildren);
      setOpenConfirmModal(true);
    } else {
      setChildren(newChildren);
    }
  };

  const confirmRemoveChildren = () => {
    setChildren(pendingChildren);
    setPendingChildren([]);
    setOpenConfirmModal(false);
  };

  const cancelRemoveChildren = () => {
    setPendingChildren([]);
    setOpenConfirmModal(false);
  };

  const isFormValid = useMemo(() => {
    return Boolean(firstName.trim() && lastName.trim() && email.trim());
  }, [firstName, lastName, email]);

  return (
    <Box p={7}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link color="text.secondary" underline="hover" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
          Kindergartens
        </Link>
        {kindergartenName && (
          <Link
            color="text.secondary"
            underline="hover"
            onClick={() => navigate(`/${kindergartenId}/details?tab=${KindergartenDetailsTabs.Parents}`)}
            sx={{ cursor: 'pointer' }}
          >
            {kindergartenName}
          </Link>
        )}
        <Typography color="text.primary">{getPageTitle()}</Typography>
      </Breadcrumbs>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="44px">
        <Typography variant="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {currentMode === ActionMode.DETAILS && (
            <CircleIcon
              style={{
                color: getStatusColor(status),
                fontSize: 12
              }}
            />
          )}
          {currentMode === ActionMode.CREATE && 'Create parent'}
          {currentMode === ActionMode.EDIT && 'Edit parent'}
          {currentMode === ActionMode.DETAILS && `${firstName} ${lastName}`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {mode === ActionMode.CREATE && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Secondary}
              variant="outlined"
              onClick={handleSaveAndCreateAnother}
              label="Save & create another"
              disabled={!isFormValid}
            />
          )}
          {isEditing && currentMode !== ActionMode.DETAILS && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Primary}
              variant="contained"
              onClick={handleSave}
              label="Save"
              disabled={!isFormValid}
            />
          )}
          {currentMode === ActionMode.DETAILS && !isEditing && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Primary}
              variant="contained"
              onClick={() => {
                setIsEditing(true);
                setCurrentMode(ActionMode.EDIT);
              }}
              label="Edit"
              startIcon={<EditIcon />}
            />
          )}
        </Box>
      </Box>

      <ProfilePageBox>
        {(mode === ActionMode.EDIT || mode === ActionMode.DETAILS) && (
          <ProfilePictureAvatar src={avatarUrl ?? undefined} alt={`${firstName} ${lastName}`} />
        )}
        <Box
          sx={{
            display: 'flex',
            width: '500px',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '20px'
          }}
        >
          <GenericTextField
            label="First name"
            required={true}
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            disabled={!isEditing}
          />
          <GenericTextField
            label="Last name"
            required={true}
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            disabled={!isEditing}
          />
          <GenericTextField
            label="Email"
            required={true}
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={mode !== ActionMode.CREATE}
          />

          <GenericTextField label="Phone" required={false} value={phone} onChange={e => setPhone(e.target.value)} disabled={!isEditing} />
          <GenericStatusSwitch label="Parent status" status={status} onChange={newStatus => setStatus(newStatus)} disabled={!isEditing} />
          {childrenOptions && (
            <GenericPeopleDropdown
              label="Children"
              options={childrenOptions}
              selectedOptions={children}
              onChange={handleChildrenChange}
              disabled={!isEditing}
              isChildren={true}
            />
          )}
        </Box>
      </ProfilePageBox>
      <GenericInfoModal
        open={openConfirmModal}
        onClose={cancelRemoveChildren}
        onConfirm={confirmRemoveChildren}
        label="Remove child(ren)"
        message={`Are you sure you want to remove child from ${firstName} ${lastName}`}
        confirmLabel="Remove"
        entityName=""
      />
    </Box>
  );
};
export default ParentPage;
